import React, { useEffect, useState, useMemo } from 'react';
import {
    Card,
    Select,
    DatePicker,
    Button,
    message,
    Table,
    Spin,
    Empty,
    Typography,
} from 'antd';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    ChartData,
    ChartOptions,
} from 'chart.js';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../utils';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { useMetaSetup } from '../context/MetaSetupContext';
import { useAuthOrg } from '../context/AuthOrgContext';
import { handleApiError } from '../utils/errorHandler';
import { Dayjs } from 'dayjs';
import {
    SyncOutlined,
    DownloadOutlined,
} from '@ant-design/icons';
import CommonLayout from '../common/LayoutBlock';
import Heading from '../common/Heading';
import '../assets/css/tablesShadow.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    ChartTooltip,
    Legend
);

const { RangePicker } = DatePicker;
const { Option } = Select;

/* ────────────────────── Types ────────────────────── */
interface BotInfo {
    botId: string;
    botName: string;
    status: boolean;
}

interface BotMessages {
    userInitiated: number;
    botInitiated: number;
    total: number;
}

interface MilestoneCount {
    milestoneNum: number;
    milestonePositioner: string;
    milestoneName: string;
    userCount: number;
}

interface BotAnalytics {
    botId: string;
    botName: string;
    live: boolean;
    milestoneUserCounts: MilestoneCount[];
    messages: BotMessages;
}

interface BotAnalyticsResponse {
    meta: {
        code: number;
        status: boolean;
        message: string;
    };
    data: {
        bots: BotInfo[];
        analytics: BotAnalytics;
    };
}

const INITIAL_BOT_ANALYTICS: BotAnalytics = {
    botId: '',
    botName: '',
    live: false,
    milestoneUserCounts: [],
    messages: { userInitiated: 0, botInitiated: 0, total: 0 },
};

const BotAnalyticsPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const { orgWabaData, fetchWabaDetails, loadingWaba } = useMetaSetup();
    const { availableOrgs, currentOrg } = useAuthOrg();

    const [loading, setLoading] = useState(false);
    const [selectedOrgId, setSelectedOrgId] = useState<string>(currentOrg?.orgId || '');
    const [selectedWabaId, setSelectedWabaId] = useState<string>('');
    const [selectedBotId, setSelectedBotId] = useState<string>('');
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [campaignId, setCampaignId] = useState<string>('');
    const [adId, setAdId] = useState<string>('');

    const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>([]);
    const [ads, setAds] = useState<{ id: string; name: string }[]>([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [loadingAds, setLoadingAds] = useState(false);

    const [bots, setBots] = useState<BotInfo[]>([]);
    const [botAnalytics, setBotAnalytics] = useState<BotAnalytics>(INITIAL_BOT_ANALYTICS);

    useEffect(() => {
        setBreadcrumbs([{ title: <div className="text-lg">Bot Analytics</div> }]);
        if (orgWabaData.length === 0) fetchWabaDetails();
    }, []);

    const filteredWabas = useMemo(() => {
        const orgData = orgWabaData.find(o => o.orgId === selectedOrgId);
        return orgData?.wabaDetails || [];
    }, [orgWabaData, selectedOrgId]);

    useEffect(() => {
        if (selectedWabaId) {
            fetchBots(selectedWabaId);
        } else {
            setBots([]);
            setSelectedBotId('');
        }
    }, [selectedWabaId]);

    const fetchBots = async (clientId: string) => {
        try {
            setLoading(true);
            const res = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/org/clients/${clientId}/bots`
            );
            if (res?.meta?.code === 200 && res.data) {
                setBots(res.data);
                const live = res.data.find((b: any) => b.status);
                const chosen = live ?? res.data[0];
                if (chosen) setSelectedBotId(chosen.botId);
            }
        } catch (error) {
            handleApiError(error, 'fetch-bots', 'Failed to load bots');
        } finally {
            setLoading(false);
        }
    };

    const fetchCampaigns = async (botId: string) => {
        try {
            setLoadingCampaigns(true);
            const res = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/util/dropdown/campaigns?botId=${botId}`
            );
            if (res?.data) {
                setCampaigns(res.data);
            } else {
                setCampaigns([]);
            }
        } catch (error) {
            handleApiError(error, 'fetch-campaigns', 'Failed to load campaigns');
            setCampaigns([]);
        } finally {
            setLoadingCampaigns(false);
        }
    };

    const fetchAds = async (campId: string) => {
        try {
            setLoadingAds(true);
            const res = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/util/dropdown/ads?campaignId=${campId}`
            );
            if (res?.data) {
                setAds(res.data);
            } else {
                setAds([]);
            }
        } catch (error) {
            handleApiError(error, 'fetch-ads', 'Failed to load ads');
            setAds([]);
        } finally {
            setLoadingAds(false);
        }
    };

    const downloadLeads = async (eventType: string) => {
        if (!selectedBotId) return;
        try {
            let body: any = {
                org_id: selectedOrgId,
                bot_id: selectedBotId,
                milestone: eventType,
                start_date: dateRange[0]?.format('YYYY-MM-DD') || null,
                end_date: dateRange[1]?.format('YYYY-MM-DD') || null,
                limit: 10000000,
                offset: 0,
            };
            if (campaignId) body.campaign_id = campaignId;
            if (adId) body.ad_id = adId;

            const res: any = await coreAPIService.post(CTW_API_ENDPOINT, '/v2/bot-analytics/leads/export-bot-milestone-leads', body);
            if (res?.data?.fileContent) {
                const { fileContent, contentType, fileName } = res.data;
                const byteCharacters = atob(fileContent);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: contentType });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${eventType}_leads.csv`;
                link.click();
                URL.revokeObjectURL(url);
                message.success('Leads exported successfully');
            } else { throw new Error(res?.meta?.message ?? 'Export failed'); }
        } catch (error) {
            handleApiError(error, 'export-leads', 'Failed to export leads');
        }

    }

    const fetchAnalytics = async () => {
        if (!selectedWabaId || !selectedBotId) {
            message.warning('Please select WABA and Bot');
            return;
        }

        try {
            setLoading(true);
            const params = new URLSearchParams();
            // params.append('botId', selectedBotId);
            params.append('orgId', selectedOrgId);
            if (dateRange[0]) params.append('startDate', dateRange[0].format('YYYY-MM-DD'));
            if (dateRange[1]) params.append('endDate', dateRange[1].format('YYYY-MM-DD'));
            if (campaignId) params.append('campaignId', campaignId);
            if (adId) params.append('adId', adId);

            const res = await coreAPIService.get<BotAnalyticsResponse>(
                CTW_API_ENDPOINT,
                `v2/bot-analytics/bot/${selectedBotId}?${params.toString()}`
            );

            if (res?.meta?.code === 200 && res.data?.analytics) {
                setBotAnalytics(res.data.analytics);
            } else {
                setBotAnalytics(INITIAL_BOT_ANALYTICS);
                message.info('No analytics data found');
            }
        } catch (error) {
            handleApiError(error, 'fetch-bot-analytics', 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedBotId) {
            fetchAnalytics();
            setCampaignId('');
            setAdId('');
            setCampaigns([]);
            setAds([]);
            fetchCampaigns(selectedBotId);
        }
    }, [selectedBotId]);

    useEffect(() => {
        setAdId('');
        setAds([]);
        if (campaignId) fetchAds(campaignId);
    }, [campaignId]);

    /* ───────────── Chart (Meta-style) ───────────── */
    const chartData = useMemo<ChartData<'bar', number[], string>>(() => {
        const sorted = [...botAnalytics.milestoneUserCounts].sort(
            (a, b) => a.milestoneNum - b.milestoneNum
        );

        // Generate gradually increasing dark colors
        const generateColors = (count: number) => {
            const colors = [];
            for (let i = 0; i < count; i++) {
                // Calculate darkness: starts lighter, gets darker
                // Using HSL for better control: hue=220 (blue-gray), saturation=15%, lightness decreases
                const lightness = 45 - (i * (40 / Math.max(count - 1, 1))); // 65% to 25%
                colors.push(`hsl(222, 15%, ${lightness}%)`);
            }
            return colors;
        };

        return {
            labels: sorted.map(i => i.milestoneName),
            datasets: [
                {
                    label: 'Users',
                    data: sorted.map(i => i.userCount || 0),
                    backgroundColor: generateColors(sorted.length),
                    borderRadius: 6,
                    barThickness: 36,
                },
            ],
        };
    }, [botAnalytics]);

    const chartOptions: ChartOptions<'bar'> = {
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: false }, ticks: { color: '#6B7280' } },
            y: { beginAtZero: true, grid: { color: '#E5E7EB' }, ticks: { color: '#6B7280' } },
        },
    };

    return (
        <CommonLayout>
            <div className="p-6 space-y-6">
                <Heading title="Bot Analytics" />

                <div className='bg-gray-50 p-4 space-y-6'>


                    {/* Filters */}
                    <Card className="shadow-none">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-8 gap-3">
                            <div>
                                <Typography.Text type="secondary" className="mb-1 text-s">Account</Typography.Text>
                                <Select value={selectedOrgId} className="w-full" onChange={setSelectedOrgId}>
                                    {availableOrgs.map(o => (
                                        <Option key={o.orgId} value={o.orgId}>{o.orgName}</Option>
                                    ))}
                                </Select>
                            </div>

                            <div>
                                <Typography.Text type="secondary" className="mb-1 text-s">WABA</Typography.Text>
                                <Select
                                    placeholder="Select WABA"
                                    value={selectedWabaId || undefined}
                                    onChange={setSelectedWabaId}
                                    loading={loadingWaba}
                                    className='w-full'
                                    virtual={false}
                                >
                                    {filteredWabas.map(w => (
                                        <Option key={w.clientId} value={w.clientId}>
                                            {w.subdomain} ({w.wabaNumber})
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div>
                                <Typography.Text type="secondary" className="mb-1 text-s">Bot</Typography.Text>
                                <Select
                                    placeholder="Select Bot"
                                    value={selectedBotId || undefined}
                                    onChange={setSelectedBotId}
                                    disabled={!selectedWabaId}
                                    className='w-full'
                                >
                                    {bots.map(b => (
                                        <Option key={b.botId} value={b.botId}>
                                            {b.botName} {b.status ? '(Live)' : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div>
                                <Typography.Text type="secondary" className="mb-1 text-s">Date Range</Typography.Text>
                                <RangePicker className="w-full" value={dateRange} onChange={d => setDateRange(d || [null, null])} />
                            </div>

                            <div className="xl:col-span-2">
                                <Typography.Text type="secondary" className="mb-1 text-s">Campaign (optional)</Typography.Text>
                                <Select
                                    placeholder="Select Campaign"
                                    value={campaignId || undefined}
                                    onChange={val => setCampaignId(val)}
                                    loading={loadingCampaigns}
                                    disabled={!selectedBotId}
                                    className="w-full"
                                    allowClear
                                    onClear={() => setCampaignId('')}
                                >
                                    {campaigns.map(c => (
                                        <Option key={c.id} value={c.id}>{c.name}</Option>
                                    ))}
                                </Select>
                            </div>

                            <div>
                                <Typography.Text type="secondary" className="mb-1 text-s">Ad (optional)</Typography.Text>
                                <Select
                                    placeholder="Select Ad"
                                    value={adId || undefined}
                                    onChange={val => setAdId(val)}
                                    loading={loadingAds}
                                    disabled={!campaignId}
                                    className="w-full"
                                    allowClear
                                    onClear={() => setAdId('')}
                                >
                                    {ads.map(a => (
                                        <Option key={a.id} value={a.id}>{a.name}</Option>
                                    ))}
                                </Select>
                            </div>

                            <div className="order-first justify-self-end md:col-start-2 xl:order-last xl:col-start-auto xl:justify-self-auto">
                                <Typography.Text className="mb-1 text-s text-white/100">Submit</Typography.Text>
                                <Button type="primary" className="bg-[#1F344C] w-auto xl:w-full" icon={<SyncOutlined spin={loading} />} onClick={fetchAnalytics}>
                                    Refresh
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {loading ? (
                        <div className="flex justify-center p-12"><Spin size="large" /></div>
                    ) : botAnalytics.botId ? (
                        <>
                            {/* KPIs */}
                            {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[
                                    ['Total Messages', botAnalytics.messages.total],
                                    ['User Initiated', botAnalytics.messages.userInitiated],
                                    ['Bot Initiated', botAnalytics.messages.botInitiated],
                                ].map(([label, value]) => (
                                    <Card key={label} className="border border-gray-200 shadow-none">
                                        <p className="text-sm text-gray-500">{label}</p>
                                        <p className="text-3xl font-semibold">{value}</p>
                                    </Card>
                                ))}
                            </div> */}

                            {/* Chart */}
                            <Card title="User Progression" className="border border-gray-200 shadow-none">
                                <div className="h-[380px]">
                                    <Bar data={chartData} options={chartOptions} />
                                </div>
                            </Card>

                            {/* Table */}
                            <Card title="Milestone Breakdown" className="border border-gray-200 shadow-none">
                                <Table
                                    dataSource={botAnalytics.milestoneUserCounts}
                                    rowKey="milestoneName"
                                    pagination={false}
                                    columns={[
                                        { title: 'Milestone', dataIndex: 'milestoneName' },
                                        { title: 'Users', dataIndex: 'userCount' },
                                        {
                                            title: 'Action',
                                            render: (_, r) => (
                                                <Button icon={<DownloadOutlined />} className='bg=[#1F344C]' size="small" onClick={() => downloadLeads(r.milestoneName)} >
                                                    Export
                                                </Button>
                                            ),
                                        },
                                    ]}
                                />
                            </Card>
                        </>
                    ) : (
                        <Empty description="Select a bot to view analytics" />
                    )}
                </div>
            </div>
        </CommonLayout>
    );
};

export default BotAnalyticsPage;
